import React, { useState, useEffect } from 'react';
import { Search, Filter, Calendar, Download, BarChart2, IndianRupee } from 'lucide-react';
import api from '../api';

export default function Reports() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ totalPayout: 0, employeeCount: 0 });

  useEffect(() => {
    fetchReports();
  }, [selectedMonth]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/reports?month=${selectedMonth}`);
      const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
      
      setReports(data);
      
      // Calculate month payout based on total earned by all employees
      let total = 0;
      data.forEach(item => {
        total += parseFloat(item.total_earned || 0);
      });
      setSummary({ totalPayout: total, employeeCount: data.length });
      
    } catch (err) {
      console.error("Failed to load reports", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = reports.filter(r => 
    (r.name && r.name.toLowerCase().includes(searchTerm.toLowerCase())) || 
    (r.employee_id && r.employee_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCSVExport = () => {
    if (filteredReports.length === 0) return;
    
    const headers = ['EMPLOYEE ID', 'EMPLOYEE NAME', 'DEPARTMENT', 'PRESENT', 'ABSENT', 'HALF DAY', 'MONTH EARNED', 'BASE SALARY', 'STATUS'];
    const csvRows = [headers.join(',')];
    
    filteredReports.forEach(r => {
      const row = [
        r.employee_id,
        `"${r.name}"`,
        r.department || 'N/A',
        r.present_days,
        r.absent_days,
        r.late_days,
        parseFloat(r.total_earned || 0),
        parseFloat(r.net_salary || 0),
        r.payment_status
      ];
      csvRows.push(row.join(','));
    });
    
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `payroll_report_${selectedMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Top Controls Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-2.5 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        
        {/* Left Side: Filters & Export */}
        <div className="flex flex-wrap items-center gap-3">
          
          {/* Search Box */}
          <div className="relative w-full sm:w-64 border border-gray-200 rounded-lg bg-gray-50/50 flex items-center shadow-sm focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all">
            <Search className="absolute left-3 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search employee..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 bg-transparent text-[13px] focus:outline-none text-slate-700 font-medium placeholder:font-normal placeholder:text-gray-400"
            />
          </div>
          
          {/* Month Filter */}
          <div className="flex items-center gap-2 px-3 lg:px-4 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm transition-colors hover:border-gray-300">
            <Filter size={15} className="text-slate-500" strokeWidth={2.5} />
            <span className="text-[13px] font-bold text-slate-600">Month:</span>
            <div className="relative flex items-center">
              <input 
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-transparent text-[13px] font-black text-[#1e293b] outline-none cursor-pointer"
              />
            </div>
          </div>
          
          {/* CSV Export */}
          <button onClick={handleCSVExport} className="flex items-center gap-2 px-4 py-1.5 bg-[#f3f4fa] border border-[#e5e7f2] font-black text-slate-600 rounded-lg text-[13px] hover:bg-[#ebedf5] transition-colors shadow-sm">
            <Download size={16} className="text-slate-500" />
            CSV Export
          </button>

        </div>

        {/* Right Side: Month Payout Summary */}
        <div className="bg-emerald-50 border border-emerald-100/60 rounded-xl px-4 py-2 flex items-center gap-5 shadow-sm min-w-[190px]">
          <div>
            <span className="text-[10px] font-black uppercase text-emerald-600 tracking-wider">Month Payout</span>
            <div className="text-xl font-black text-slate-800 tracking-tight leading-tight mt-0.5">
              ₹{summary.totalPayout.toLocaleString('en-IN')}
            </div>
            <span className="text-[11px] font-semibold text-slate-500 block">for {summary.employeeCount} Employees</span>
          </div>
          <div className="w-10 h-10 rounded-lg bg-white border border-emerald-100 flex items-center justify-center shadow-sm text-emerald-500 ml-auto">
            <BarChart2 size={20} strokeWidth={2.5} />
          </div>
        </div>

      </div>

      {/* Main Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#f8fafc] border-b border-gray-200 text-[11px] uppercase font-black text-[#6082b6] tracking-wider whitespace-nowrap shadow-[0_1px_2px_-1px_rgba(0,0,0,0.05)]">
                <th className="px-6 py-3 lg:py-3.5">EMPLOYEE</th>
                <th className="px-4 py-3 lg:py-3.5">DEPARTMENT</th>
                <th className="px-4 py-3 lg:py-3.5 text-center">PRESENT</th>
                <th className="px-4 py-3 lg:py-3.5 text-center">ABSENT</th>
                <th className="px-4 py-3 lg:py-3.5 text-center">HALF DAY</th>
                <th className="px-4 py-3 lg:py-3.5">MONTH EARNED</th>
                <th className="px-4 py-3 lg:py-3.5">BASE SALARY</th>
                <th className="px-4 py-3 lg:py-3.5">STATUS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-20 text-center text-slate-400 font-bold">
                    <div className="flex flex-col items-center gap-3">
                      <IndianRupee className="animate-bounce text-slate-300" size={32} />
                      Loading Reports Data...
                    </div>
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-20 text-center text-slate-400 font-bold">
                    No reports match your search criteria.
                  </td>
                </tr>
              ) : (
                filteredReports.map((record, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50 transition-colors group cursor-default border-b border-gray-100">
                    
                    {/* EMPLOYEE */}
                    <td className="px-6 py-3 min-w-[200px]">
                      <div className="text-[13px] font-bold text-slate-800 leading-tight">{record.name}</div>
                      <div className="text-[11px] font-semibold text-gray-400 mt-0.5">{record.employee_id}</div>
                    </td>

                    {/* DEPARTMENT */}
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold text-slate-600">
                        {record.department || 'N/A'}
                      </span>
                    </td>

                    {/* PRESENT */}
                    <td className="px-4 py-3 text-center bg-emerald-50/30">
                      <span className="text-sm font-black text-emerald-600">
                        {record.present_days}
                      </span>
                    </td>

                    {/* ABSENT */}
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-black text-red-500">
                        {record.absent_days}
                      </span>
                    </td>

                    {/* HALF DAY */}
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-black text-amber-500">
                        {record.late_days}
                      </span>
                    </td>

                    {/* MONTH EARNED */}
                    <td className="px-4 py-3">
                      <span className="text-[13px] font-black text-blue-600 tracking-wide">
                        ₹{parseFloat(record.total_earned || 0).toLocaleString('en-IN')}
                      </span>
                    </td>

                    {/* BASE SALARY */}
                    <td className="px-4 py-3">
                      <span className="text-[13px] font-bold text-slate-600 tracking-wide">
                        ₹{parseFloat(record.net_salary || 0).toLocaleString('en-IN')}
                      </span>
                    </td>

                    {/* STATUS */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex justify-center items-center w-[76px] px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase shadow-sm border ${
                        record.payment_status === 'Paid' 
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                          : record.payment_status === 'Delayed'
                          ? 'bg-red-100 text-red-700 border-red-200'
                          : 'bg-amber-100 text-amber-700 border-amber-200'
                      }`}>
                        {record.payment_status}
                      </span>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
